import React from 'react';
import { formatNumber } from '../../../utilities/Helpers';

// Table columns for Predictions
export const PredictionColumns = [
  { Header: 'Freq (Hz)', accessor: 'Frequency' },
  { Header: 'PIP (psig)', accessor: 'PumpIntakePressure' },
  { Header: 'Pwf (psig)', accessor: 'Pwf' },
  { Header: 'Total Rate (stb/d)', accessor: 'FlowrateOutput' },
  { Header: 'Oil Rate (stbopd)', accessor: 'OilRate' },
  { Header: 'Inc. Oil (stbopd)', accessor: 'IncrementalOil' },
  { Header: 'Drawdown (%)', accessor: 'DrawDown' },
];

export function mapPredictionsData(Predictions: any) {
  if (!Array.isArray(Predictions.PredictionList)) return [];
  const count = Predictions.PredictionCount || Predictions.PredictionList.length;
  return Predictions.PredictionList.map((item: any, idx: number) => {
    const freq = formatNumber(item.Frequency, 1);
    return {
      Frequency: count > 1 ? `${freq}${idx > 0 ? ' (+' + String(idx) + ')' : ''}` : freq,
      PumpIntakePressure: formatNumber(item.PumpIntakePressure, 0),
      Pwf: formatNumber(item.Pwf, 0),
      FlowrateOutput: formatNumber(item.FlowrateOutput, 0),
      OilRate: formatNumber(item.OilRate, 0),
      IncrementalOil: formatNumber(item.IncrementalOil, 0),
      DrawDown: formatNumber(item.DrawDown, 1),
    };
  });
}

export const RecommendationsColumns = [
  {
    Header: 'Task',
    id: 'diagnosis-recommendation',
    accessor: '',
    width: 80,
    Cell: ({ row }: { row: any }) => {
      const handleClick = () => {
        const baseUrl = window.location.origin;
        const isESPWidget = 'Frequency' in row.original || 'NewFrequency' in row.original;
        const targetUrl = isESPWidget
          ? `${baseUrl}/pumpchecker/Checker/Well/Recommendations`
          : `${baseUrl}/pumpchecker/Checker/GasLiftReport/Recommendations`;
        window.open(targetUrl, '_blank', 'noopener,noreferrer');
      };
      return (
        <button className='recommend-button' onClick={handleClick}>
          Recommend
        </button>
      );
    },
  },
  { Header: 'Frequency (Hz)', accessor: 'Frequency' },
  { Header: 'New Frequency (Hz)', accessor: 'NewFrequency' },
  { Header: 'FC Additional Oil (stb/d)', accessor: 'FrequencyChangeAdditionalOil' },
];

export function getRecommendationsData(recommendationsData: any) {
  if (!recommendationsData) return [];
  return [
    {
      Frequency: formatNumber(recommendationsData.Frequency, 1),
      NewFrequency: formatNumber(recommendationsData.NewFrequency, 1),
      FrequencyChangeAdditionalOil: formatNumber(recommendationsData.FrequencyChangeAdditionalOil, 0),
    },
  ];
}

// Table columns for WellTestData1
export const WellTestData1Columns = [
  { Header: 'Oil (stb/d)', accessor: 'Oil' },
  { Header: 'Water (stb/d)', accessor: 'Water' },
  { Header: 'Gas (mscf/d)', accessor: 'Gas' },
  { Header: 'Liq Rate (stb/d)', accessor: 'LiquidFlowrate' },
  { Header: 'WC (%)', accessor: 'WaterCut' },
  { Header: 'Total GOR (scf/stb)', accessor: 'TotalGasOilRatio' },
];
export const WellTestData2Columns = [
  { Header: 'THP (psig)', accessor: 'WellheadPressure' },
  { Header: 'Calc WHT (deg F)', accessor: 'ResultWellheadTemperature' },
  { Header: 'WHT (deg F)', accessor: 'WellheadTemperature' },
  { Header: 'Liq Rate (stb/d)', accessor: 'PumpFrequency' },
  { Header: 'PIP (psig)', accessor: 'PumpIntakePressure' },
  { Header: 'PIT (deg F)', accessor: 'PumpIntakeTemperature' },
];
export const WellTestData3Columns = [
  { Header: 'PI (stb/d/psi)', accessor: 'ProductivityIndex' },
  { Header: 'Pwf (psig)', accessor: 'ResultsPwf' },
  { Header: 'Pr (psig)', accessor: 'ReservoirPressureStatic' },
  { Header: 'Drdn (%)', accessor: 'DrawDown' },
];
export function getWellTestData(wellData: any) {
  if (!wellData) return [];
  return [
    {
      Oil: formatNumber(wellData.Oil, 1),
      Water: formatNumber(wellData.Water, 1),
      Gas: formatNumber(wellData.Gas, 1),
      LiquidFlowrate: formatNumber(wellData.LiquidFlowrate, 1),
      WaterCut: formatNumber(wellData.WaterCut, 1),
      TotalGasOilRatio: formatNumber(wellData.TotalGasOilRatio, 1),
      WellheadPressure: formatNumber(wellData.WellheadPressure),
      ResultWellheadTemperature: formatNumber(wellData.ResultWellheadTemperature),
      WellheadTemperature: formatNumber(wellData.WellheadTemperature),
      PumpFrequency: formatNumber(wellData.PumpFrequency, 1),
      PumpIntakePressure: formatNumber(wellData.PumpIntakePressure, 1),
      PumpIntakeTemperature: formatNumber(wellData.PumpIntakeTemperature, 1),
      ProductivityIndex: formatNumber(wellData.ProductivityIndex, 4),
      ResultsPwf: formatNumber(wellData.ResultsPwf),
      ReservoirPressureStatic: formatNumber(wellData.ReservoirPressureStatic),
      DrawDown: formatNumber(wellData.PredictionsCatalogue?.PredictionList?.[0]?.DrawDown, 1),
    },
  ];
}

export const ResultColumns = [
  { Header: 'Parameter', accessor: 'Parameter' },
  { Header: 'Calculated', accessor: 'Calculated' },
  { Header: 'Measured', accessor: 'Measured' },
  { Header: 'Difference', accessor: 'Difference' },
];

export function mapResultData(resultData: any) {
  if (!resultData) return [];
  const calc = (a: any, b: any, decimals = 1) => (a == null || b == null || isNaN(a) || isNaN(b)) ? '' : formatNumber(Number(a) - Number(b), decimals);
  const btn = (
    <button className='recommend-button' disabled style={{ opacity: 0.3, cursor: 'not-allowed' }}>Calculate</button>
  );
  return [
    { Parameter: 'Pump Discharge Pressure (psig)', Calculated: formatNumber(resultData.CalculatedPumpDischargePressure, 0), Measured: formatNumber(resultData.PumpDischargePressure, 0), Difference: calc(resultData.PumpDischargePressure, resultData.CalculatedPumpDischargePressure, 0) },
    { Parameter: 'Pump Intake Pressure (psig)', Calculated: formatNumber(resultData.CalculatedPumpIntakePressure, 0), Measured: formatNumber(resultData.PumpIntakePressure, 0), Difference: calc(resultData.PumpIntakePressure, resultData.CalculatedPumpIntakePressure, 0) },
    { Parameter: 'Pump dP (psi)', Calculated: calc(resultData.CalculatedPumpDischargePressure, resultData.CalculatedPumpIntakePressure, 0), Measured: calc(resultData.CalculatedPumpDischargePressure, resultData.PumpIntakePressure, 0), Difference: (() => { const a = Number(resultData.CalculatedPumpDischargePressure) - Number(resultData.CalculatedPumpIntakePressure); const b = Number(resultData.CalculatedPumpDischargePressure) - Number(resultData.PumpIntakePressure); return formatNumber(a - b, 0); })() },
    { Parameter: 'Pump Rate (bpd)', Calculated: formatNumber(resultData.IntersectFlowrate, 0), Measured: formatNumber(resultData.IPHCMedianFlowrate, 0), Difference: calc(resultData.IPHCMedianFlowrate, resultData.IntersectFlowrate, 0) },
    { Parameter: 'Pump Degradation (%)', Calculated: formatNumber(resultData.PumpDegradation, 0), Measured: '', Difference: '' },
    { Parameter: 'Pump dP Reduction (%)', Calculated: formatNumber(resultData.PumpDpPercentageReduction, 1), Measured: '', Difference: '' },
    { Parameter: 'In Range?', Calculated: resultData.ResultsInRange != null ? String(resultData.ResultsInRange) : '', Measured: '', Difference: '' },
    { Parameter: 'Calculated Pwf (psig)', Calculated: formatNumber(resultData.ResultsPwf, 0), Measured: '', Difference: '' },
    { Parameter: 'Lost Gross Rate (stblpd)', Calculated: formatNumber(resultData.LostGrossRate, 0), Measured: '', Difference: '' },
    { Parameter: 'Lost Oil (stbopd)', Calculated: formatNumber(resultData.LostOil, 0), Measured: '', Difference: '' },
    { Parameter: 'Hz for 800 psi deadhead:', Calculated: btn, Measured: '', Difference: '' },
    { Parameter: 'Min Hz to surface fluid:', Calculated: btn, Measured: '', Difference: '' },
  ];
}
